import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PASSWORD_RULES } from '@/lib/passwordRules';
import { useStaffSession } from '@/lib/useStaffSession';
import supabase from '@/lib/supabaseClient';
import { CheckCircle2, MapPin, Monitor, Shield, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const SecurityTab = () => {
  const staffSession = useStaffSession();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const allPasswordRulesPassed = PASSWORD_RULES.every(r => r.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleChangePassword = async () => {
    if (!allPasswordRulesPassed || !passwordsMatch) return;
    setPasswordSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordSaving(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Password updated successfully');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  if (staffSession.active) {
    return (
      <p className="text-sm" data-testid="security-staff-session-note">
        Password changes are not available during a staff session. The client can reset their own password from the sign-in page.
      </p>
    );
  }

  return (
    <div className="space-y-8">
      {/* Change Password */}
      <div>
        <h3 className="text-lg font-medium mb-4">Change Password</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="security-new-pw">New Password</Label>
            <Input
              id="security-new-pw"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
              className="mt-2"
            />
          </div>

          {newPassword.length > 0 && (
            <ul className="space-y-1" role="list" aria-label="Password requirements">
              {PASSWORD_RULES.map(rule => {
                const passed = rule.test(newPassword);
                return (
                  <li key={rule.label} className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-muted-foreground'}`}>
                    {passed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {rule.label}
                  </li>
                );
              })}
            </ul>
          )}

          <div>
            <Label htmlFor="security-confirm-pw">Confirm Password</Label>
            <Input
              id="security-confirm-pw"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
              className="mt-2"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-destructive mt-1">Passwords do not match</p>
            )}
          </div>

          <Button
            onClick={handleChangePassword}
            disabled={!allPasswordRulesPassed || !passwordsMatch || passwordSaving}
          >
            {passwordSaving ? 'Updating\u2026' : 'Update Password'}
          </Button>
        </div>
      </div>

      <hr className="border-border" />

      {/* Active Sessions — placeholder */}
      <div>
        <h3 className="text-lg font-medium mb-2">Active Sessions</h3>
        <p className="text-sm text-muted-foreground mb-4">Devices currently signed in to your account.</p>
        <div className="border border-border rounded-lg divide-y divide-border">
          <div className="flex items-center gap-4 p-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Current Session</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" /> This device &middot; Active now
              </p>
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-50 dark:bg-green-950 px-2 py-1 rounded-full">Current</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
          <Shield className="w-3.5 h-3.5" />
          Full session management and login history coming soon.
        </p>
      </div>
    </div>
  );
};

export default SecurityTab;
